import FilterListIcon from '@mui/icons-material/FilterList';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { IClinic } from '../../interfaces/clinic';
import {
  DocumentListFilters,
  DocumentProcessingStatus,
  DocumentReviewState,
  DocumentSource,
  ProcessingStep,
} from '../../interfaces/document';
import {
  getReviewStatePresentation,
  isNeedingAttentionSelection,
  REVIEW_STATE_PRESENTATION,
  REVIEW_STATES_NEEDING_ATTENTION,
} from './shared';

interface DocumentsFiltersProps {
  filters: DocumentListFilters;
  searchDocumentId: string;
  searchCrmId: string;
  searchPatientName: string;
  clinics: Partial<IClinic>[];
  onFilterChange: (key: keyof DocumentListFilters, value: any) => void;
  /**
   * Sets the review filter and refetches in one step. Separate from `onFilterChange` because
   * a quick view is a single action, and making an operator press Apply afterwards is how a
   * one-click work queue turns into a three-click one.
   */
  onApplyQuickView: (reviewStates?: DocumentReviewState[]) => void;
  onSearchChange: (field: 'documentId' | 'crmId' | 'patientName', value: string) => void;
  onApplyFilters: () => void;
}

const DocumentsFilters: React.FC<DocumentsFiltersProps> = ({
  filters,
  searchDocumentId,
  searchCrmId,
  searchPatientName,
  clinics,
  onFilterChange,
  onApplyQuickView,
  onSearchChange,
  onApplyFilters,
}) => {
  // Active only when the filter is exactly the attention set, so the button reflects the
  // view rather than merely whether some review filter happens to be set.
  const isNeedsAttentionActive = isNeedingAttentionSelection(filters.reviewStates);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onApplyFilters();
    }
  };

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {/* Search Section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Document ID
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder='Document ID...'
              value={searchDocumentId}
              onChange={(e) => onSearchChange('documentId', e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Leadsquared ID
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder='Leadsquared ID...'
              value={searchCrmId}
              onChange={(e) => onSearchChange('crmId', e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Patient Name
            </Typography>
            <TextField
              fullWidth
              size='small'
              placeholder='Patient Name...'
              value={searchPatientName}
              onChange={(e) => onSearchChange('patientName', e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Processing Status
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.processingStatuses || []}
                onChange={(e) => onFilterChange('processingStatuses', e.target.value)}
                renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
                sx={{
                  color:
                    filters.processingStatuses?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                {Object.values(DocumentProcessingStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Processing Step
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.processingSteps || []}
                onChange={(e) => onFilterChange('processingSteps', e.target.value)}
                renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
                sx={{
                  color: filters.processingSteps?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                {Object.values(ProcessingStep).map((step) => (
                  <MenuItem key={step} value={step}>
                    {step}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Source
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.sourceTypes || []}
                onChange={(e) => onFilterChange('sourceTypes', e.target.value)}
                renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
                sx={{
                  color: filters.sourceTypes?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                {Object.values(DocumentSource).map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Document Type
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.documentTypes || []}
                onChange={(e) => onFilterChange('documentTypes', e.target.value)}
                renderValue={(selected) => (selected.length === 0 ? 'All' : selected.join(', '))}
                sx={{
                  color: filters.documentTypes?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                <MenuItem value='referral-order'>Referral Order</MenuItem>
                <MenuItem value='prior-authorization'>Prior Authorization</MenuItem>
                <MenuItem value='lab-results'>Lab Results</MenuItem>
                <MenuItem value='medical-records'>Medical Records</MenuItem>
                <MenuItem value='insurance'>Insurance</MenuItem>
                <MenuItem value='unknown'>Unknown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Receiving Clinic
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.receivingClinicIds || []}
                onChange={(e) => onFilterChange('receivingClinicIds', e.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? 'All' : `${selected.length} selected`
                }
                sx={{
                  color:
                    filters.receivingClinicIds?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                {clinics.map((clinic) => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Detected Clinic
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                multiple
                displayEmpty
                value={filters.detectedClinicIds || []}
                onChange={(e) => onFilterChange('detectedClinicIds', e.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? 'All' : `${selected.length} selected`
                }
                sx={{
                  color:
                    filters.detectedClinicIds?.length === 0 ? 'text.secondary' : 'text.primary',
                }}>
                {clinics.map((clinic) => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Has Referrals
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                displayEmpty
                value={filters.hasReferrals ?? ''}
                onChange={(e) =>
                  onFilterChange(
                    'hasReferrals',
                    e.target.value === '' ? undefined : e.target.value === 'true',
                  )
                }
                renderValue={(selected) => {
                  if (selected === '') return 'All';
                  if (selected === true) return 'With Referrals';
                  return 'Without Referrals';
                }}
                sx={{
                  color: filters.hasReferrals === undefined ? 'text.secondary' : 'text.primary',
                }}>
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='true'>With Referrals</MenuItem>
                <MenuItem value='false'>Without Referrals</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
              Requires Review
            </Typography>
            <FormControl fullWidth size='small'>
              <Select
                displayEmpty
                value={filters.requiresReview ?? ''}
                onChange={(e) =>
                  onFilterChange(
                    'requiresReview',
                    e.target.value === '' ? undefined : e.target.value === 'true',
                  )
                }
                renderValue={(selected) => {
                  if (selected === '') return 'All';
                  if (selected === true) return 'Needs Review';
                  return 'No Review';
                }}
                sx={{
                  color: filters.requiresReview === undefined ? 'text.secondary' : 'text.primary',
                }}>
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='true'>Needs Review</MenuItem>
                <MenuItem value='false'>No Review</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} alignItems='end'>
        <Grid item xs={12} sm={6} md={2}>
          <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
            Is Processed
          </Typography>
          <FormControl fullWidth size='small'>
            <Select
              displayEmpty
              value={filters.isProcessed ?? ''}
              onChange={(e) =>
                onFilterChange(
                  'isProcessed',
                  e.target.value === '' ? undefined : e.target.value === 'true',
                )
              }
              renderValue={(selected) => {
                if (selected === '') return 'All';
                if (selected === true) return 'Processed';
                return 'Not Processed';
              }}
              sx={{
                color: filters.isProcessed === undefined ? 'text.secondary' : 'text.primary',
              }}>
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='true'>Processed</MenuItem>
              <MenuItem value='false'>Not Processed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='subtitle2' sx={{ mb: 1, fontSize: '12px', fontWeight: 600 }}>
            Review
          </Typography>
          <FormControl fullWidth size='small'>
            <Select
              multiple
              displayEmpty
              value={filters.reviewStates ?? []}
              onChange={(e) => onFilterChange('reviewStates', e.target.value)}
              renderValue={(selected) =>
                (selected as DocumentReviewState[]).length === 0
                  ? 'All'
                  : (selected as DocumentReviewState[])
                      // Guarded, because `reviewStates` comes off the URL unvalidated: an old
                      // bookmark or a value a newer API sends before this build knows it would
                      // index to `undefined` here and take the whole filter panel down.
                      .map((state) => getReviewStatePresentation(state).label)
                      .join(', ')
              }
              sx={{
                color: (filters.reviewStates ?? []).length === 0 ? 'text.secondary' : 'text.primary',
              }}>
              {Object.values(DocumentReviewState).map((state) => (
                <MenuItem key={state} value={state}>
                  {REVIEW_STATE_PRESENTATION[state].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant='contained'
            startIcon={<FilterListIcon />}
            onClick={onApplyFilters}
            fullWidth
            sx={{ height: 40 }}>
            Apply
          </Button>
        </Grid>
      </Grid>

      {/*
        The single place a failed or incomplete document gets worked, which the product brief
        asks for by name: "a single list of referrals needing attention that intake can open
        and work through". It covers `failed` as well as `needs-review` — a document that
        produced nothing is the case that used to disappear from both existing filters.
      */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
        <Typography variant='caption' color='text.secondary'>
          Quick views:
        </Typography>
        <Button
          size='small'
          variant={isNeedsAttentionActive ? 'contained' : 'outlined'}
          startIcon={<ReportProblemOutlinedIcon />}
          onClick={() =>
            onApplyQuickView(
              isNeedsAttentionActive ? undefined : REVIEW_STATES_NEEDING_ATTENTION,
            )
          }>
          Needs attention
        </Button>
        {isNeedsAttentionActive && (
          <Typography variant='caption' color='text.secondary'>
            Showing documents that failed or need review.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DocumentsFilters;
